import axiosInstance from "@/config/axiosInstance";
import { getBillingApiRoute } from "@/helpers/api.util";
import { getServerNow } from "@/services/publicAuthConfigService";

function getRemainingDaysFromTimestamp(timestamp) {
    if (!timestamp) return 0;

    var expiresAt = Number(timestamp);
    if (!Number.isFinite(expiresAt)) {
        expiresAt = new Date(timestamp).getTime();
    }
    if (!Number.isFinite(expiresAt) || expiresAt <= 0) return 0;

    var remainingMs = expiresAt - getServerNow();
    if (remainingMs <= 0) return 0;

    return Math.floor(remainingMs / (1000 * 60 * 60 * 24)) + 1;
}

/**
 * Open a payment gateway (IPG) for subscription or ticket purchase.
 *
 * @param {object} params
 * @param {string} [params.subscriptionKey] - Subscription plan name
 * @param {string} [params.ticketKey] - Ticket slug (for ticket purchases)
 * @param {string} [params.discountCode] - Optional discount code
 * @param {string} [params.device] - Device type ('web', 'android', 'ios')
 * @param {number} [params.cardNumber] - Optional saved card number
 * @param {boolean} [params.useTokens] - Apply token balance as discount
 * @param {string} token - User access token
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export async function openPaymentGateway(params, token) {
    var body = {};
    if (params.subscriptionKey) body.subscriptionKey = params.subscriptionKey;
    if (params.ticketKey) body.ticketKey = params.ticketKey;
    if (params.discountCode) body.discountCode = params.discountCode;
    if (params.device) body.device = params.device;
    if (params.cardNumber) body.cardNumber = params.cardNumber;
    if (params.useTokens) body.useTokens = params.useTokens;

    return axiosInstance.post(getBillingApiRoute("payment"), body, {
        headers: { Authorization: "Bearer " + token },
        validateStatus: false,
    });
}

/**
 * Get discount codes for the current user.
 *
 * @param {object} params
 * @param {string} [params.discountCode] - Specific code to look up
 * @param {string} [params.userKey] - User key filter
 * @param {number} [params.pageNumber] - Page number (default 1)
 * @param {number} [params.pageIndex] - Page size (default 10)
 * @param {string} token - User access token
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export async function getDiscountCodes(params, token) {
    var query = {};
    if (params.pageNumber) query.pageNumber = params.pageNumber;
    if (params.pageIndex) query.pageIndex = params.pageIndex;
    if (params.discountCode) query.discountCode = params.discountCode;
    if (params.userKey) query.userKey = params.userKey;

    return axiosInstance.get(getBillingApiRoute("discount-code"), {
        params: query,
        headers: { Authorization: "Bearer " + token },
    });
}

/**
 * Get available subscription plans (public, no auth needed).
 *
 * @param {number} [pageNumber=1]
 * @param {number} [pageIndex=20]
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export async function getSubscriptions(pageNumber, pageIndex) {
    return axiosInstance.get(getBillingApiRoute("subscription"), {
        params: {
            pageNumber: pageNumber || 1,
            pageIndex: pageIndex || 20,
        },
    });
}

/**
 * Check the user's active subscription status.
 * Uses the dedicated current-subscription endpoint which returns
 * the active subscription with remainingAccessDays directly.
 *
 * @returns {Promise<number>} Days remaining on active subscription, or 0
 */
export async function getUserSubscriptionStatus() {
    try {
        var response = await axiosInstance.get(
            getBillingApiRoute("payment/subscription/current")
        );
        var data = response.data?.data;

        if (!data) {
            return 0;
        }

        if (typeof data.remainingAccessDays === "number") {
            return Math.max(0, data.remainingAccessDays);
        }

        return getRemainingDaysFromTimestamp(
            data.expireAt || data.subscriptionInfo?.expireDate
        );
    } catch (err) {
        console.log("Subscription status get error", err);
        return 0;
    }
}

